// Cómo se describe Videos cortos ante el agente (ADR-2026-10-30-videos-cortos-con-el-agente): la
// ficha de PANTALLA ACTUAL, sin campos (se trabaja con las herramientas).
//  • `estado` va en CADA turno, recortado a 600 caracteres: lo justo para orientarse.
//  • `notas` llega entero con `ver_pantalla`: cada producto con su id, su público, su rubro, su
//    idea, su voz y su estado, y la lista de voces para `elegir_voz`.
// Puro.
import type { DescripcionDePantalla } from '../../../../../chat-atlas';
import { QUE_HACE_EL_MODO } from '../../../puente/camposDeModo';
import { cambioDesdeElVideo, costoDeLaTanda, costoPorLanzar, queFalta, sinLocucionConVoz, videosEnCurso, videosPorLanzar, vozDelProducto, type PlanDeVideos, type ProductoDeLaTanda } from '../plan';
import type { VozDeVideo, VozDelEstudio } from '../videosCortosApi';

export const PANTALLA = 'el modo Videos cortos';

export interface DatosParaDescribir {
  ruta: string;
  plan: PlanDeVideos | null;
  voces: VozDelEstudio[];
  enSesion: boolean;
  escribiendo: boolean;
  generando: boolean;
}

/** Lo editado después de que salió el video, para que el agente sepa si alcanza con rehacer la voz. Puro. */
const textoDelCambio = (c: ReturnType<typeof cambioDesdeElVideo>): string =>
  c === 'voz' ? ', le cambiaron la locución o la voz (alcanza con solo_voz)' : c === 'video' ? ', le cambiaron el prompt (hay que rehacerlo entero)' : '';

const ESTADO_CORTO: Record<ProductoDeLaTanda['estado'], string> = {
  'sin-guion': 'sin guion',
  escrito: 'guion listo',
  en_cola: 'en la fila',
  generando: 'creando el video',
  listo: 'video listo',
  fallo: 'falló',
  cancelado: 'cancelado',
};

/** Cómo se lee una voz ("Malena (mujer, español argentino)", "Automática", "Sin voz"). Puro. */
export function nombreDeVoz(voz: VozDeVideo | null, voces: VozDelEstudio[]): string {
  if (voz === null) return 'Sin voz';
  if (voz === 'auto') return 'Automática (según el producto)';
  if (voz === 'mujer') return 'Valeria (mujer)';
  if (voz === 'hombre') return 'Facundo (hombre)';
  const v = voces.find((x) => x.id === voz);
  if (!v) return 'Una voz elegida';
  const sexo = v.sexo === 'Femenina' ? 'mujer' : v.sexo === 'Masculina' ? 'hombre' : v.clonada ? 'clonada' : null;
  return `${v.nombre}${sexo ? ` (${sexo}${v.idioma ? `, ${v.idioma}${v.acento ? ` ${v.acento.toLowerCase()}` : ''}` : ''})` : ''}`;
}

const corta = (s: string | null | undefined, max: number): string => {
  const t = (s ?? '').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

export function describirVideosCortos(d: DatosParaDescribir): DescripcionDePantalla {
  const queHace = QUE_HACE_EL_MODO['videos-cortos'] ?? 'Un video corto por producto, planeado con el agente.';
  if (!d.plan) {
    return {
      pantalla: PANTALLA,
      ruta: d.ruta,
      queHace,
      campos: [],
      faltaParaGenerar: ['elegir productos (la portada)'],
      estado: d.enSesion ? 'La sesión está cargando.' : 'La portada de Videos cortos: la persona elige productos y toca Adelante (o hace aclaraciones); con eso se crea la sesión. Todavía no hay lote.',
    };
  }
  const plan = d.plan;
  const faltas = queFalta(plan);
  const escritos = plan.productos.filter((p) => !!p.guion).length;
  const conVozPropia = plan.productos.filter((p) => p.voz !== undefined).length;
  const sinLocucion = plan.productos.filter((p) => sinLocucionConVoz(plan, p)).length;
  const listos = plan.productos.filter((p) => p.estado === 'listo').length;
  const porLanzar = videosPorLanzar(plan).length;
  const estado = [
    `«${plan.titulo}»: ${plan.productos.length} producto${plan.productos.length === 1 ? '' : 's'}; guiones ${escritos}/${plan.productos.length}${sinLocucion ? ` (${sinLocucion} con voz y sin locución: reescribir)` : ''}${listos ? `; ${listos} video${listos === 1 ? '' : 's'} listo${listos === 1 ? '' : 's'}` : ''}${videosEnCurso(plan) ? `; ${videosEnCurso(plan)} creándose` : ''}.`,
    `Voz del lote: ${nombreDeVoz(plan.voz, d.voces)}${conVozPropia ? ` (${conVozPropia} con voz propia)` : ''}; subtítulos ${plan.voz && plan.subtitulos ? 'sí' : 'no'}.`,
    `Crear lanza ${porLanzar} video${porLanzar === 1 ? '' : 's'} = ${costoPorLanzar(plan)} créditos (el lote entero, ${costoDeLaTanda(plan)}).`,
    plan.indicacion.trim() ? `Indicación: ${corta(plan.indicacion, 100)}.` : '',
    d.escribiendo ? 'El escritor está escribiendo.' : d.generando ? 'Creando los videos.' : faltas.length ? `Falta: ${faltas.slice(0, 2).join('; ')}${faltas.length > 2 ? '…' : ''}.` : porLanzar ? 'Listo para crear (espera el click).' : 'Nada por lanzar.',
    'Ids, públicos y voces: ver_pantalla. Guiones enteros: ver_guiones.',
  ]
    .filter(Boolean)
    .join(' ')
    .slice(0, 600);

  const notas: string[] = [
    'PRODUCTOS (id=id de producto para las herramientas):',
    ...plan.productos.map((p) => {
      const g = p.guion;
      const detalle = g
        ? ` · público: ${g.publico ?? '?'} · rubro: ${g.rubro ?? '?'} · idea: ${corta(g.idea, 90) || '(sin idea)'}${g.locucion ? '' : ' · SIN locución'}`
        : '';
      return `- id=${p.varianteId} · ${p.nombre} · ${ESTADO_CORTO[p.estado]}${p.fijado ? ', fijado' : ''}${p.rehacer ? (p.soloVoz ? ', por rehacer la voz' : ', por rehacer') : ''}${textoDelCambio(cambioDesdeElVideo(plan, p))}${p.motivo ? ` (${corta(p.motivo, 80)})` : ''} · voz: ${nombreDeVoz(vozDelProducto(plan, p), d.voces)}${p.voz === undefined ? ' [la del lote]' : ''}${p.notas ? ` · notas: ${corta(p.notas, 80)}` : ''}${detalle}`;
    }),
    `VOCES (para elegir_voz, por id o nombre; "auto" y "sin" también valen):`,
    ...d.voces.map((v) => `- ${v.id} · ${v.nombre}${v.clonada ? ' · clonada de la marca' : ` · ${v.sexo ?? '?'}${v.idioma ? ` · ${v.idioma}${v.acento ? ` ${v.acento}` : ''}` : ''}`}${v.descripcion && !v.descripcion.includes('SILENT') ? ` · ${corta(v.descripcion, 70)}` : ''}`),
    `PEDIDO ORIGINAL: ${plan.pedido || '(vacío)'}`,
    `INDICACIÓN GENERAL: ${plan.indicacion || '(vacía)'}`,
  ];

  return {
    pantalla: PANTALLA,
    ruta: d.ruta,
    queHace,
    campos: [],
    faltaParaGenerar: faltas,
    costo: `${costoPorLanzar(plan)} créditos por lanzar (${porLanzar} videos × 12)`,
    estado,
    notas,
  };
}
